import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useSellerRaffles } from '../hooks/useDashboard';
import { Button } from '../components/ui';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/Tabs';
import Badge from '../components/ui/Badge';
import Spinner from '../components/ui/Spinner';
import EmptyState from '../components/shared/EmptyState';
import ProgressBar from '../components/raffle/ProgressBar';
import CountdownTimer from '../components/raffle/CountdownTimer';

/**
 * My Competitions Page
 *
 * Lists all competitions created by the user (seller view).
 */
export default function MyCompetitions() {
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = searchParams.get('filter') || 'all';

  const { raffles, allRaffles, stats, loading, error } = useSellerRaffles({
    status: statusFilter === 'all' ? undefined : statusFilter,
  });

  const handleFilterChange = (value) => {
    if (value === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ filter: value });
    }
  };

  return (
    <div className="bg-midnight min-h-screen">
      {/* Header */}
      <div className="bg-midnight-light border-b border-cream/5">
        <div className="page-container py-8">
          {/* Back link */}
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 text-sm text-cream/60 hover:text-cream mb-4 transition-colors"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Back to Dashboard
          </Link>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 text-center sm:text-left">
            <div>
              <h1 className="font-display font-bold text-2xl md:text-3xl text-cream">
                My Competitions
              </h1>
              <p className="text-cream/60 mt-1">
                {allRaffles.length} {allRaffles.length === 1 ? 'competition' : 'competitions'} created
              </p>
            </div>
            <Link to="/create" className="self-center sm:self-auto">
              <Button variant="accent">Create Competition</Button>
            </Link>
          </div>

          {/* Stats Row */}
          {allRaffles.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
              <StatCard
                label="Total Earnings"
                value={formatCurrency(stats.totalEarnings)}
                color="success"
              />
              <StatCard
                label="Entries Sold"
                value={stats.totalEntriesSold.toLocaleString()}
                color="primary"
              />
              <StatCard
                label="Completion Rate"
                value={`${stats.completionRate}%`}
                color="default"
              />
              <StatCard
                label="Active"
                value={stats.active}
                color="accent"
              />
            </div>
          )}

          {/* Filter Tabs */}
          <div className="mt-6 -mb-px flex justify-center sm:justify-start">
            <Tabs value={statusFilter} onValueChange={handleFilterChange}>
              <TabsList className="bg-transparent border-none p-0 gap-0">
                <FilterTab value="all" label="All" count={allRaffles.length} active={statusFilter === 'all'} />
                <FilterTab value="active" label="Active" count={stats.active} active={statusFilter === 'active'} />
                <FilterTab value="completed" label="Completed" count={stats.completed} active={statusFilter === 'completed'} />
                <FilterTab value="cancelled" label="Cancelled" count={stats.cancelled} active={statusFilter === 'cancelled'} />
              </TabsList>
            </Tabs>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="page-container py-8">
        {loading ? (
          <div className="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-error">Error loading competitions</p>
          </div>
        ) : raffles.length === 0 ? (
          <EmptyState
            icon={PlusIcon}
            title={statusFilter === 'all' ? 'No competitions yet' : `No ${statusFilter} competitions`}
            description={
              statusFilter === 'all'
                ? "You haven't created any competitions yet. List your first item to get started!"
                : `You don't have any ${statusFilter} competitions.`
            }
            actionLabel="Create Competition"
            onAction={() => window.location.href = '/create'}
          />
        ) : (
          <div className="space-y-4">
            {raffles.map((raffle) => (
              <RaffleCard key={raffle.id} raffle={raffle} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function FilterTab({ value, label, count, active }) {
  return (
    <TabsTrigger
      value={value}
      className={`px-4 py-3 rounded-none border-b-2 flex items-center gap-2 ${
        active
          ? 'border-primary text-cream bg-transparent'
          : 'border-transparent text-cream/60 hover:text-cream bg-transparent'
      }`}
    >
      {label}
      {count > 0 && (
        <span className={`text-xs px-1.5 py-0.5 rounded-full ${
          active ? 'bg-primary/20 text-primary' : 'bg-midnight-lighter text-cream/50'
        }`}>
          {count}
        </span>
      )}
    </TabsTrigger>
  );
}

function StatCard({ label, value, color }) {
  const colorStyles = {
    default: 'text-cream',
    primary: 'text-primary',
    accent: 'text-accent',
    success: 'text-success',
  };

  return (
    <div className="bg-midnight-lighter/50 rounded-lg p-4 border border-cream/5">
      <p className="text-xs text-cream/50 uppercase tracking-wider">{label}</p>
      <p className={`text-xl font-bold font-mono mt-1 ${colorStyles[color]}`}>{value}</p>
    </div>
  );
}

function RaffleCard({ raffle }) {
  const photo = raffle.photos?.[0];
  const progress = raffle.ticketQuantity > 0
    ? (raffle.ticketsSold / raffle.ticketQuantity) * 100
    : 0;
  const revenue = (raffle.ticketsSold || 0) * (raffle.ticketPrice || 0);

  return (
    <Link
      to={`/competition/${raffle.id}`}
      className="group block bg-midnight-light/50 hover:bg-midnight-light rounded-xl border border-cream/5 hover:border-primary/20 transition-all overflow-hidden"
    >
      <div className="flex flex-col sm:flex-row">
        {/* Photo */}
        <div className="sm:w-48 h-40 sm:h-auto overflow-hidden bg-midnight-lighter shrink-0">
          {photo ? (
            <img
              src={photo}
              alt={raffle.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ImageIcon className="w-12 h-12 text-cream/20" />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 p-4 sm:p-6">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="min-w-0">
              <h3 className="font-semibold text-lg text-cream truncate group-hover:text-primary transition-colors">
                {raffle.title}
              </h3>
              <p className="text-sm text-cream/50 mt-0.5">
                {raffle.pickupArea} • {raffle.category}
              </p>
            </div>
            <RaffleStatusBadge status={raffle.status} />
          </div>

          {/* Progress (for active raffles) */}
          {raffle.status === 'active' && (
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-cream/60">
                  {raffle.ticketsSold || 0} / {raffle.ticketQuantity} entries sold
                </span>
                <span className="text-cream/60">{Math.round(progress)}%</span>
              </div>
              <ProgressBar
                soldCount={raffle.ticketsSold || 0}
                totalCount={raffle.ticketQuantity}
                threshold={raffle.minimumThreshold}
                showThreshold
              />
            </div>
          )}

          {/* Stats Row */}
          <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-sm">
            <div>
              <span className="text-cream/50">Entry Price</span>
              <span className="text-cream font-mono font-medium ml-2">
                {formatCurrency(raffle.ticketPrice)}
              </span>
            </div>
            <div>
              <span className="text-cream/50">Revenue</span>
              <span className="text-success font-mono font-medium ml-2">
                {formatCurrency(revenue)}
              </span>
            </div>
            {raffle.status === 'active' && raffle.endsAt && (
              <div className="flex items-center gap-2">
                <ClockIcon className="w-4 h-4 text-cream/50" />
                <CountdownTimer endsAt={raffle.endsAt} compact />
              </div>
            )}
            {raffle.status === 'completed' && raffle.winnerId && (
              <div className="flex items-center gap-2 text-accent">
                <TrophyIcon className="w-4 h-4" />
                <span>Winner selected</span>
              </div>
            )}
          </div>

          {/* Date info */}
          <p className="text-xs text-cream/40 mt-4">
            Created {formatDate(raffle.createdAt)}
            {raffle.status === 'completed' && raffle.completedAt && (
              <> • Ended {formatDate(raffle.completedAt)}</>
            )}
          </p>
        </div>
      </div>
    </Link>
  );
}

function RaffleStatusBadge({ status }) {
  const config = {
    active: { label: 'Live', variant: 'active' },
    pending: { label: 'Pending', variant: 'warning' },
    completed: { label: 'Completed', variant: 'success' },
    cancelled: { label: 'Cancelled', variant: 'error' },
    draft: { label: 'Draft', variant: 'default' },
  };

  const { label, variant } = config[status] || config.draft;

  return <Badge variant={variant} size="sm">{label}</Badge>;
}

// Helpers
function formatCurrency(cents) {
  if (!cents) return '€0';
  return `€${(cents / 100).toLocaleString('en-IE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function formatDate(timestamp) {
  if (!timestamp) return 'Unknown';
  const date = timestamp.seconds
    ? new Date(timestamp.seconds * 1000)
    : new Date(timestamp);
  return date.toLocaleDateString('en-IE', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

// Icons
function PlusIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
    </svg>
  );
}

function ImageIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  );
}

function ClockIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function TrophyIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3h14M5 3a2 2 0 00-2 2v1a3 3 0 003 3h1m11-6a2 2 0 012 2v1a3 3 0 01-3 3h-1m-5 0v6m-3 0h6m-3-6a3 3 0 01-3-3V5a2 2 0 012-2h2a2 2 0 012 2v4a3 3 0 01-3 3z" />
    </svg>
  );
}

function ArrowLeftIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
    </svg>
  );
}
